import { components } from "@/lib/ApiSchema";
import Button from "@/lib/Button";
import Input from "@/lib/Input";
import PasswordHint from "@/lib/PasswordHint";
import ProviderIcon from "@/lib/ProviderIcon";
import $api from "@/lib/queries/openapi";
import useUploadFiles from "@/lib/queries/useUploadFiles";
import SettingsSectionLayout from "@/lib/SettingsSectionLayout";
import { getProviderName } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";

export default function Profile({
  user,
}: {
  user: components["schemas"]["LoggedInUserSchema"];
}) {
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarId, setAvatarId] = useState<string>();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const queryClient = useQueryClient();

  const { data: connectedAccounts } = $api.useQuery(
    "get",
    "/api/_allauth/browser/v1/account/providers",
    { initialData: { status: 200, data: [] } },
  );

  const { mutate: disconnectAccount } = $api.useMutation(
    "delete",
    "/api/_allauth/browser/v1/account/providers",
    {
      onSuccess: () => {
        toast.success("Account disconnected successfully");
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/_allauth/browser/v1/account/providers"],
        });
      },
    },
  );

  const { mutate: changePassword, isPending: isChangingPassword } =
    $api.useMutation("post", "/api/auth/password/change", {
      onSuccess: () => {
        toast.success("Password changed successfully");
      },
    });

  const { uploadFiles, isUploading: isUploadingAvatar } = useUploadFiles({
    onSuccess: (fileIds) => {
      setAvatarId(fileIds[0]);
    },
  });

  const { mutate: updateUser, isPending: isUpdatingUser } = $api.useMutation(
    "patch",
    "/api/user",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["get", "/api/user"] });
        toast.success("User updated successfully");
        setAvatarId(undefined);
      },
    },
  );

  const { mutate: logout, isPending: isLoggingOut } = $api.useMutation(
    "post",
    "/api/auth/logout",
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
      },
    },
  );

  const changePasswordValid =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    newPassword === confirmNewPassword;

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-y-auto bg-slate-100 p-4">
      <SettingsSectionLayout title="Profile">
        <>
          <Input
            label="Email"
            value={user.email}
            onChange={() => {}}
            disabled
            width={inputWidth}
          />
          <Input
            label="Name"
            value={name}
            onChange={setName}
            width={inputWidth}
          />
          <div className="flex flex-col gap-4 text-sm">
            <div>Profile Picture</div>
            <div className="flex items-center gap-4">
              <img
                className="rounded-full size-14"
                src={avatar ? URL.createObjectURL(avatar) : user.avatar_url}
                alt="Profile Avatar"
              />
              <label className="underline text-blue-600 cursor-pointer">
                <input
                  className="hidden"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setAvatar(file); // for fast preview only
                      uploadFiles([file]);
                    }
                  }}
                />
                Change Picture
              </label>
            </div>
          </div>
          <Button
            primary
            disabled={name === user.name && !avatarId}
            loading={isUpdatingUser || isUploadingAvatar}
            onClick={() => updateUser({ body: { name, avatar_id: avatarId } })}
          >
            Save Changes
          </Button>
        </>
      </SettingsSectionLayout>
      <SettingsSectionLayout title="Connected Accounts">
        {connectedAccounts?.data && connectedAccounts.data.length > 0 ? (
          <div className="flex flex-col gap-3">
            {connectedAccounts.data.map((account) => {
              return (
                <div
                  key={account.uid}
                  className="flex items-center justify-between gap-4 p-4 bg-white rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <ProviderIcon
                      provider={account.provider.id}
                      className="size-6"
                    />
                    <div className="flex flex-col">
                      <div className="font-medium capitalize">
                        {getProviderName(account.provider)}
                        <div className="text-sm text-gray-500">
                          {account.display}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                      Connected
                    </span>
                    <Button
                      secondary
                      onClick={() =>
                        disconnectAccount({
                          body: {
                            account: account.uid,
                            provider: account.provider.id,
                          },
                        })
                      }
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 py-4">No connected accounts</p>
        )}
      </SettingsSectionLayout>
      <SettingsSectionLayout title="Change Password">
        <>
          <Input
            label="Current Password"
            value={currentPassword}
            onChange={setCurrentPassword}
            type="password"
            width={inputWidth}
          />
          <Input
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            type="password"
            width={inputWidth}
          />
          <PasswordHint />
          <Input
            label="Confirm New Password"
            value={confirmNewPassword}
            onChange={setConfirmNewPassword}
            type="password"
            width={inputWidth}
          />
          <Button
            primary
            disabled={!changePasswordValid}
            loading={isChangingPassword}
            onClick={() =>
              changePassword({
                body: {
                  new_password: newPassword,
                  current_password: currentPassword,
                },
              })
            }
          >
            Change Password
          </Button>
        </>
      </SettingsSectionLayout>
      <SettingsSectionLayout title="Logout">
        <Button secondary onClick={() => logout({})} loading={isLoggingOut}>
          Log out of account
        </Button>
      </SettingsSectionLayout>
    </div>
  );
}

const inputWidth = 500;
